import axios from 'axios';
import apiClient from '../../../services/api';

export type FarmWeatherCondition =
  | 'rain'
  | 'drizzle'
  | 'thunderstorm'
  | 'snow'
  | 'clear'
  | 'clouds'
  | 'atmosphere'
  | 'unknown';

export type FarmCurrentWeather = {
  farm_id: string;
  lat?: number | null;
  lon?: number | null;
  weather_main?: string | null;
  weather_id?: number | null;
  description?: string | null;
  temperature_c?: number | null;
  wind_speed_ms?: number | null;
  condition: FarmWeatherCondition;
  is_precipitating: boolean;
  rain_mm_1h?: number | null;
};

/** UI mood for the Saved Fields strip (heat is temp-derived). */
export type WeatherMood = 'clear' | 'cloudy' | 'rain' | 'heat' | 'snow' | 'storm';

/** Hot enough for a heat mood in PH field conditions (°C). */
export const WEATHER_HEAT_TEMP_C = 33;

export function weatherMood(
  weather: FarmCurrentWeather | null | undefined
): WeatherMood | null {
  if (!weather) return null;
  if (weather.condition === 'thunderstorm') return 'storm';
  if (weather.condition === 'snow') return 'snow';
  if (
    weather.is_precipitating ||
    weather.condition === 'rain' ||
    weather.condition === 'drizzle'
  ) {
    return 'rain';
  }
  const temp = weather.temperature_c;
  if (typeof temp === 'number' && temp >= WEATHER_HEAT_TEMP_C) return 'heat';
  if (weather.condition === 'clear') return 'clear';
  if (weather.condition === 'clouds' || weather.condition === 'atmosphere') return 'cloudy';
  if (typeof temp === 'number') return 'clear';
  return null;
}

export function formatTempC(temp: number | null | undefined): string | null {
  if (typeof temp !== 'number' || Number.isNaN(temp)) return null;
  return `${Math.round(temp)}°`;
}

export function formatWindMs(speed: number | null | undefined): string | null {
  if (typeof speed !== 'number' || Number.isNaN(speed)) return null;
  return `${speed < 10 ? speed.toFixed(1) : Math.round(speed)} m/s`;
}

type CacheEntry = {
  expiresAt: number;
  data: FarmCurrentWeather | null;
};

const CACHE_TTL_MS = 10 * 60 * 1000;
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<FarmCurrentWeather | null>>();

function normalizeCondition(raw: unknown): FarmWeatherCondition {
  const v = String(raw || '').toLowerCase();
  if (
    v === 'rain' ||
    v === 'drizzle' ||
    v === 'thunderstorm' ||
    v === 'snow' ||
    v === 'clear' ||
    v === 'clouds' ||
    v === 'atmosphere'
  ) {
    return v;
  }
  return 'unknown';
}

export function weatherIntensity(
  weather: FarmCurrentWeather | null | undefined
): 'light' | 'medium' | 'heavy' {
  if (!weather?.is_precipitating) return 'light';
  if (weather.condition === 'thunderstorm') return 'heavy';
  if (weather.condition === 'drizzle') return 'light';
  const mm = weather.rain_mm_1h;
  if (typeof mm === 'number') {
    if (mm >= 4) return 'heavy';
    if (mm >= 1) return 'medium';
    return 'light';
  }
  return 'medium';
}

export async function fetchFarmCurrentWeather(
  farmId: number | string
): Promise<FarmCurrentWeather | null> {
  const id = String(farmId);
  if (!id || id.startsWith('uploaded_field_') || id.startsWith('demo_')) {
    return null;
  }

  const now = Date.now();
  const hit = cache.get(id);
  if (hit && hit.expiresAt > now) {
    return hit.data;
  }

  const existing = inflight.get(id);
  if (existing) return existing;

  const req = (async () => {
    try {
      const res = await apiClient.get<FarmCurrentWeather>(
        `/farm-current-weather/${encodeURIComponent(id)}/`,
        { timeout: 15000 }
      );
      const data = res.data
        ? {
            ...res.data,
            farm_id: res.data.farm_id ?? id,
            condition: normalizeCondition(res.data.condition),
            is_precipitating: Boolean(res.data.is_precipitating),
          }
        : null;
      cache.set(id, { data, expiresAt: now + CACHE_TTL_MS });
      return data;
    } catch (e) {
      // Soft-fail: cards stay normal if weather is unavailable
      if (axios.isAxiosError(e) && e.response?.status !== 404) {
        console.warn('Farm current weather failed:', id, e.response?.status || e.message);
      }
      cache.set(id, { data: null, expiresAt: now + 2 * 60 * 1000 });
      return null;
    } finally {
      inflight.delete(id);
    }
  })();

  inflight.set(id, req);
  return req;
}

/** Fetch weather for visible saved-field IDs (capped). */
export async function fetchFarmCurrentWeatherMap(
  farmIds: Array<number | string>,
  maxIds = 9
): Promise<Record<string, FarmCurrentWeather>> {
  const unique = Array.from(
    new Set(
      farmIds
        .map((id) => String(id))
        .filter((id) => id && !id.startsWith('uploaded_field_') && !id.startsWith('demo_'))
    )
  ).slice(0, maxIds);

  const results = await Promise.all(
    unique.map(async (id) => {
      const data = await fetchFarmCurrentWeather(id);
      return [id, data] as const;
    })
  );

  const out: Record<string, FarmCurrentWeather> = {};
  for (const [id, data] of results) {
    if (data) out[id] = data;
  }
  return out;
}
